/**
 * Single source of truth for how imported imagery fills its element box.
 *
 * Both renderers MUST consume this so the web preview === the compiled export
 * (Epic 07 P1.3): `apps/web/src/components/preview/ImageElement.tsx` and
 * `packages/banner-core/src/compiler/element-renderer.ts`. Previously they
 * disagreed (`fill` vs `cover`) so a banner looked different in Preview than in
 * the exported HTML.
 *
 * `cover` fills the box and crops overflow — matching Figma's image-fill
 * scaleMode. (A future epic may make this per-element; today it is global.)
 */
export const IMAGE_OBJECT_FIT = 'cover';
const CSS_GENERIC_FAMILIES = new Set([
    'serif',
    'sans-serif',
    'monospace',
    'cursive',
    'fantasy',
    'system-ui',
    'ui-serif',
    'ui-sans-serif',
    'ui-monospace',
    'inherit',
    'initial',
]);
/**
 * Build a safe CSS `font-family` value from an imported font name. Both
 * renderers MUST use it so preview === export (Epic 07).
 *
 * Guards two failure modes that made headings fall back to the browser default
 * serif instead of the design's sans:
 *  1. A multi-word family ("Suisse Intl") emitted unquoted is invalid CSS in a
 *     declaration string → the whole declaration is dropped.
 *  2. A family not loaded as a webfont with no generic fallback → the browser
 *     picks serif, not the sans the design intended.
 *
 * So we single-quote the family (single quotes survive inside a double-quoted
 * HTML `style="…"` attribute and a React inline-style string) and append a
 * `sans-serif` fallback. Bare generic keywords pass through untouched.
 */
export function cssFontFamily(family) {
    const f = (family ?? '').trim();
    if (!f)
        return SUBSTITUTE_STACK;
    if (CSS_GENERIC_FAMILIES.has(f.toLowerCase()))
        return f;
    const clean = f.replace(/['"]/g, '');
    // A design that already uses the substitute needs it named once, not twice.
    if (clean.toLowerCase() === SUBSTITUTE_FONT_FAMILY.toLowerCase())
        return SUBSTITUTE_STACK;
    return `'${clean}', ${SUBSTITUTE_STACK}`;
}
/**
 * THE FONT NOBODY HAS, DRAWN DIFFERENTLY EVERYWHERE (2026-09-01).
 *
 * A design's real face is often licensed and never uploaded — `campaign_fonts` is empty
 * for every campaign in this database, so EVERY text in every banner falls through to
 * the tail of this stack. Until now that tail was the generic `sans-serif`, which each
 * environment resolves for itself: the owner's browser picks one face, the rasterizer's
 * headless Chromium another. Same definition, two different widths.
 *
 * Measured consequences, all three from one session: a URL the export renders complete
 * at 92px of ink and the Preview shows cut; a hug-label repair that estimated 111px
 * against a real 92px and slid a label 19px further than it needed; and three separate
 * rounds of chasing a "cut link" that only ever existed in one of the two renderers.
 *
 * Naming a REAL family here fixes all three at once, because the family is one the
 * free-font machinery already knows how to fetch (`KNOWN_FREE_FONT_FAMILIES`) and both
 * renderers already route through this function — its own doc has said "Both renderers
 * MUST use it so preview === export" since Epic 07. Inter is the pick: nine static
 * weights, so any weight a design uses resolves without synthesis.
 *
 * This does NOT claim to look like the licensed face. It claims that whatever is drawn,
 * is drawn the same everywhere and can therefore be measured.
 */
export const SUBSTITUTE_FONT_FAMILY = 'Inter';
/** The tail every `cssFontFamily` result ends with: the substitute, then the generic. */
export const SUBSTITUTE_STACK = `'${SUBSTITUTE_FONT_FAMILY}', sans-serif`;
